import moment from 'moment';
import { ResultHistory } from '../../../../../services/MedicalResultsService';

export const ANA_SCREEN_CODE = '42254-3';
export const LOIC_SYSTEM = 'http://loinc.org';

export const getObservationValue = (
  observation: fhir.Observation,
): number | string | undefined => {
  return observation.valueQuantity?.value ?? observation.valueString;
};

/** Comment text on the observation (STU3 `comment`, or R4-style `note` when present). */
export const getObservationComment = (
  observation: fhir.Observation,
): string | undefined => {
  const fromComment = observation.comment?.trim();
  if (fromComment) {
    return fromComment;
  }
  const notes = (observation as fhir.Observation & { note?: fhir.Annotation[] })
    .note;
  if (!notes?.length) {
    return undefined;
  }
  const texts = notes
    .map(n => n.text?.trim())
    .filter((t): t is string => Boolean(t));
  if (!texts.length) {
    return undefined;
  }
  return texts.join('\n\n');
};

export const getObservationValueDisplay = (
  observation: fhir.Observation,
): string => {
  let value = '';
  const valueQuantity =
    observation.valueQuantity || observation.component?.[0]?.valueQuantity;
  if (valueQuantity && valueQuantity.value) {
    const numericValue = Math.round(valueQuantity.value * 100) / 100;
    const comparator = valueQuantity.comparator ?? '';
    const formattedComparator = comparator === 'ad' ? 'ad ' : comparator;
    value = `${formattedComparator}${numericValue}`;
  } else if (observation.valueString) {
    value = observation.valueString;
  } else if (observation.valueCodeableConcept) {
    value =
      observation.valueCodeableConcept.text ??
      observation.valueCodeableConcept.coding?.[0]?.display ??
      '';
  }
  return value.replace('<=', '≤').replace('>=', '≥');
};

/** Longest-first so `>=` / `<=` match before `>` / `<`. */
const VALUE_STRING_NUMERIC_COMPARATORS = ['>=', '<=', '>', '<', 'ad'] as const;

function parseNumberLoose(n: number | string): number | null {
  const num = typeof n === 'number' ? n : parseFloat(String(n));
  if (isNaN(num)) return null;
  return parseFloat(num.toPrecision(4));
}

/**
 * True when `valueString` parses as a numeric observation (comparators, ratio, plain number).
 * Matches chart/scatter parsing so UI treatment stays consistent.
 */
function valueStringParsesAsNumericValue(raw: string): boolean {
  const valueString = raw.trim();
  if (!valueString) return false;

  for (const comp of VALUE_STRING_NUMERIC_COMPARATORS) {
    if (valueString.startsWith(comp)) {
      const numericPart = valueString.substring(comp.length).trim();
      return parseNumberLoose(numericPart) !== null;
    }
  }

  if (valueString.includes(':')) {
    const [numerator, denominator] = valueString
      .split(':')
      .map(parseNumberLoose);
    return numerator !== null && denominator !== null;
  }

  return parseNumberLoose(valueString) !== null;
}

/**
 * True when the observation's primary displayed value comes from `valueString`
 * (not `valueQuantity` or `valueCodeableConcept`). Mirrors `getObservationValueDisplay` precedence.
 * Value strings that parse as numeric (comparators, ratios, plain numbers) are treated like numeric values.
 */
export const observationPrimaryValueIsValueString = (
  observation: fhir.Observation,
): boolean => {
  const valueQuantity =
    observation.valueQuantity || observation.component?.[0]?.valueQuantity;
  if (
    valueQuantity &&
    valueQuantity.value !== undefined &&
    valueQuantity.value !== null
  ) {
    return false;
  }
  if (observation.valueCodeableConcept) {
    return false;
  }
  const raw = observation.valueString;
  if (!raw?.trim()) {
    return false;
  }
  if (valueStringParsesAsNumericValue(raw)) {
    return false;
  }
  return true;
};

export const getObservationDate = (
  observation: fhir.Observation,
): string | undefined => {
  const date =
    observation.effectiveDateTime ||
    observation.effectivePeriod?.end ||
    observation.effectivePeriod?.start;
  if (date) {
    return moment(date).format('LL');
  }
  return undefined;
};

export const getObservationMMMYYYYDate = (
  observation: fhir.Observation,
): string | undefined => {
  const date =
    observation.effectiveDateTime ||
    observation.effectivePeriod?.end ||
    observation.effectivePeriod?.start;
  if (date) {
    return moment(date).format('MMM YYYY');
  }
  return undefined;
};

export const getObservationHHmmDate = (
  observation: fhir.Observation,
): string | undefined => {
  const date =
    observation.effectiveDateTime ||
    observation.effectivePeriod?.end ||
    observation.effectivePeriod?.start;
  if (date) {
    return moment(date).format('HH:mm A');
  }
  return undefined;
};

export const getObservationUnit = (observation: fhir.Observation) => {
  return (
    observation.valueQuantity?.unit?.trim() ||
    observation.component?.[0]?.valueQuantity?.unit?.trim() ||
    ''
  );
};

const isUnknownUnit = (unit: string) => unit.trim() === '';

export const sortObservationUnits = (units: string[]) =>
  [...units].sort((a, b) => {
    const aIsUnknown = isUnknownUnit(a);
    const bIsUnknown = isUnknownUnit(b);

    if (aIsUnknown && !bIsUnknown) return 1;
    if (!aIsUnknown && bIsUnknown) return -1;
    return a.localeCompare(b);
  });

export const getObservationUnitCode = (observation: fhir.Observation) => {
  return (
    observation.valueQuantity?.code?.trim() ||
    observation.component?.[0]?.valueQuantity?.code?.trim() ||
    ''
  );
};

export const getObservationUnitSystem = (observation: fhir.Observation) => {
  return (
    observation.valueQuantity?.system ||
    observation.component?.[0]?.valueQuantity?.system ||
    ''
  );
};

const UNIT_MAP: Record<string, string[]> = {
  '%': ['%', '% of total Hgb'],
  'uIU/mL': ['uIU/mL', 'uU/L'],
  'mg/dL': ['mg/dL (calc)', 'mg/dL'],
};

export const areUnitsEqual = (
  observation1: fhir.Observation,
  observation2: fhir.Observation,
) => {
  const [code1, system1] = [
    getObservationUnitCode(observation1),
    getObservationUnitSystem(observation1),
  ];
  const [code2, system2] = [
    getObservationUnitCode(observation2),
    getObservationUnitSystem(observation2),
  ];

  const hasCoding = [code1, system1, code2, system2].every(Boolean);

  if (hasCoding && code1 === code2 && system1 === system2) {
    return true;
  }

  const unit1 = getObservationUnit(observation1);
  const unit2 = getObservationUnit(observation2);

  if (unit1 === unit2) {
    return true;
  }

  if (UNIT_MAP[unit1]?.includes(unit2) || UNIT_MAP[unit2]?.includes(unit1)) {
    return true;
  }

  return false;
};

export const getObservationNumericValue = (
  observation: fhir.Observation,
): number | null => {
  if (observation?.valueQuantity?.value !== undefined) {
    return observation.valueQuantity.value;
  }
  if (observation?.component?.length && observation.component.length > 0) {
    return (
      getObservationNumericValue(
        observation.component[0] as fhir.Observation,
      ) ?? null
    );
  }
  // Sometimes observations are beyond the physical detection limit of the labs
  // in those cases, a valueString will be given (ex: '<3'). This returns the number from the value string
  if (
    observation.valueString &&
    (observation.valueString.startsWith('<') ||
      observation.valueString.startsWith('>'))
  ) {
    const value = parseFloat(observation.valueString.slice(1));
    if (isNaN(value)) return null;
    return value;
  }

  return null;
};

export const hasCoding = ({
  observation,
  code,
  system,
}: {
  observation: fhir.Observation;
  code: string;
  system: string;
}) =>
  observation.code?.coding?.some(
    coding => coding.code === code && coding.system === system,
  );

export const groupMedicalResultsByUnit = (
  medicalResults: ResultHistory[],
): Record<string, ResultHistory[]> => {
  const groupedResults: ResultHistory[][] = [];

  medicalResults.forEach(result => {
    const index = groupedResults.findIndex(r =>
      r.some(r => areUnitsEqual(r.resource, result.resource)),
    );
    if (index === -1) {
      groupedResults.push([result]);
    } else {
      groupedResults[index].push(result);
    }
  });

  const resultsByUnit: Record<string, ResultHistory[]> = {};

  groupedResults.forEach(results => {
    const resultWithUnit =
      results.find(r => {
        const unit = getObservationUnit(r.resource);
        return unit !== undefined && unit !== '';
      }) ?? results[0];
    const unit = getObservationUnit(resultWithUnit.resource);
    resultsByUnit[unit] = results;
  });

  return resultsByUnit;
};
